/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { FC, useContext, useEffect, useState } from 'react';
import { Button, Icon, Select, Tab } from '@scuf/common';
import './SharedTabs.scss';
import { useTranslation } from '@scuf/localization';
import { IAnyObject } from '../PerformanceGraph/IPerformanceGraph';
import { ApiContext } from '../../../context/ApiContext';
import _ from 'lodash';
import { TrainingData } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import moment from 'moment';
import descriptors from '../../../locales/en-US/descriptors';
import { returnFormattedDateTime } from '../Utils/Utils';
import { EnergyUsageDataSourceType } from '../Enums';

const SharedTabs = (props: IAnyObject) => {
  const {
    displayTabs,
    ActiveIndex,
    setOpenmodal,
    baseLineIconCheck,
    enableConsumptionSourceSelect,
    indexDisableEnergyDataSource,
    hideDataSourceSelectForIndices,
    cemsIntervalEnergySourceOption
  } = props;
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const [lastTrainedData, setLastTrainedData] = useState<TrainingData[]>([]);
  const {
    getBaselineTrainedData,
    pointHistoryUploadToggle,
    dataSourceType,
    setDataSourceType
  } = useContext(ApiContext);

  const apiContext = useContext(ApiContext);
  const { selectedDateFormat, selectedTimeFormat } = apiContext;
  const fetchData = async () => {
    try {
      if (getBaselineTrainedData) {
        const res = await getBaselineTrainedData();
        const data = res.data;
        if (data) {
          setLastTrainedData(data);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    fetchData();
  }, [baseLineIconCheck]);
  const lastBaseLineDate = baseLineIconCheck
    ? _.maxBy(
        lastTrainedData.filter((e: { trainingStatus: string }) => {
          return e.trainingStatus === 'Completed';
        }),
        (e) => {
          return new Date(e.completedOn);
        }
      )?.completedOn
    : '';
  const [currentIndex, setCurrentIndex] = useState(ActiveIndex ?? 0);

  useEffect(() => {
    if (
      enableConsumptionSourceSelect &&
      indexDisableEnergyDataSource?.length > 0 &&
      indexDisableEnergyDataSource?.includes(currentIndex)
    ) {
      setDataSourceType &&
        setDataSourceType(EnergyUsageDataSourceType.LIVE_METER);
    }
  }, [
    currentIndex,
    indexDisableEnergyDataSource,
    enableConsumptionSourceSelect
  ]);

  return (
    <div className="emass-detailed-tabs-wrapper">
      {dataSourceType !== EnergyUsageDataSourceType.UTILITY_METER &&
        baseLineIconCheck && (
          <div className="pointHistoryUploadAreasSharedTab">
            {pointHistoryUploadToggle && (
              <span className="Meter-baseline-button">
                {lastBaseLineDate && (
                  <span className="baseline-date">
                    <Icon name="badge-check" root="common" color="green" />
                    {t(d.LAST_BASELINE_TRAINED_DATE)}{' '}
                    {/* {moment(lastBaseLineDate).format('ddd, DD MMM YYYY HH:mm')} */}
                    {moment(lastBaseLineDate).format(
                      selectedDateFormat + ' ' + selectedTimeFormat
                    )}
                  </span>
                )}
                <Icon
                  root="common"
                  name="graph-drop"
                  size="medium"
                  onClick={() => setOpenmodal(true)}
                />
                <span
                  className="button-content"
                  style={{ padding: '0 10px' }}
                  onClick={() => setOpenmodal(true)}
                >
                  {t(d.baselinebutton)}
                </span>
              </span>
            )}
          </div>
        )}

      {enableConsumptionSourceSelect &&
        !_.includes(hideDataSourceSelectForIndices, currentIndex) && (
          <Select
            className="kpi-card-select emaas-data-source-select"
            options={[
              {
                value: EnergyUsageDataSourceType.UTILITY_METER,
                text: t(d.UTILITYPROVIDERNEW),
                // Disable utility meter for water tab
                disabled:
                  indexDisableEnergyDataSource?.length > 0 &&
                  indexDisableEnergyDataSource?.includes(currentIndex)
              },
              ...(cemsIntervalEnergySourceOption
                ? [
                    {
                      value: EnergyUsageDataSourceType.UTILITY_INTERVAL,
                      text: t(d.UTILITYINTERVAL),
                      disabled:
                        indexDisableEnergyDataSource?.length > 0 &&
                        indexDisableEnergyDataSource?.includes(currentIndex)
                    }
                  ]
                : []),
              {
                value: EnergyUsageDataSourceType.LIVE_METER,
                text: t(d.LIVEREADING)
              }
            ]}
            value={dataSourceType}
            defaultValue={EnergyUsageDataSourceType.UTILITY_METER}
            onChange={(value) => setDataSourceType && setDataSourceType(value)}
          />
        )}
      <Tab
        className="emass-detailed-tabs"
        defaultActiveIndex={ActiveIndex ?? 0}
        {...props}
        onTabChange={(v) => setCurrentIndex(v)}
      >
        {displayTabs.map(
          (
            _tab: {
              className: string;
              title: string;
              component: FC;
              disabled?: boolean;
            },
            index: number
          ) => (
            <Tab.Pane
              disabled={_tab.disabled ? _tab.disabled : false}
              className={_tab.className}
              title={_tab.title}
              key={index}
            >
              <_tab.component {...props} />
            </Tab.Pane>
          )
        )}
      </Tab>
    </div>
  );
};

export default SharedTabs;
